import type {
  BrowseCollectionsV1Element,
  Course,
  CourseOrS12n,
  CoursesAndRhymeProject,
  EnrichedCatalogResultQuery,
  EnrichedEntry,
  Entry,
  Offering,
} from 'bundles/browse/types/catalog-results';

function filterNull<T>(x: T | null): x is T {
  return x !== null;
}

function transformRhymeProject(courses: Course[]) {
  return (
    courses?.map((course) => {
      const metadata = course?.courseTypeMetadata?.courseTypeMetadata;
      return {
        isRhymeProject: !!metadata && 'rhymeProject' in metadata && !!metadata.rhymeProject?.typeNameIndex,
        ...course,
      };
    }) || []
  );
}

const enrichCatalogResult = (catalogResult?: BrowseCollectionsV1Element): EnrichedCatalogResultQuery => {
  if (!catalogResult) return catalogResult;
  const { entries = [], courses, s12ns, ...rest } = catalogResult;
  const elements: (Course | null)[] = courses?.elements || [];
  const filteredCourse: Course[] = elements.filter(filterNull);
  const coursesAndRhymeProjects: CoursesAndRhymeProject[] = transformRhymeProject(filteredCourse);
  const coursesAndS12ns: CourseOrS12n[] = [...(coursesAndRhymeProjects || []), ...(s12ns?.elements || [])].filter(
    filterNull
  );

  function enrichEntries<T extends Entry>(entriesToBeMapped: T[]) {
    const mappedEntries = entriesToBeMapped?.map((entry) => {
      const courseOrS12n = coursesAndS12ns?.find((c) => c?.id === entry.id);
      if (courseOrS12n) {
        return { ...entry, ...courseOrS12n };
      }
      return null;
    });
    return mappedEntries;
  }

  const enrichedEntries: (EnrichedEntry | null)[] = enrichEntries<Entry>(entries);
  const enrichedAndFilteredEntries: EnrichedEntry[] = enrichedEntries?.filter(filterNull);

  return { entries: enrichedAndFilteredEntries, ...rest };
};

const enrichCatalogResults = (catalogResults: BrowseCollectionsV1Element[]) => catalogResults?.map(enrichCatalogResult);

export { enrichCatalogResult, enrichCatalogResults };

export type { EnrichedEntry, EnrichedCatalogResultQuery, BrowseCollectionsV1Element, Offering };
